import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";

import moment from "moment";
import Moment from "react-moment";

import {
  makeStyles,
  withStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Paper,
  InputBase,
  Fab,
  CircularProgress,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import CachedIcon from "@material-ui/icons/Cached";

import { refreshPoolLP } from "../apiFunctions";
import { quoteRemoveLiquidity } from "../Liquidity/LiquidityFunctions";
import { getAmountOut, getBalanceAndSymbol } from "../ethereumFunctions";
import {
  getEarned,
  getStakedBefore,
  harvestToken,
  parseTokenDecimal,
  stackToken,
} from "./StackingFunctions";

import UnStackDialog from "./UnStackDialog";
import LoadingButton from "../Components/LoadingButton";

import useIcon from "../hooks/useIcon";

import "./Stacking.scss";
import { Link } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  primaryText: {
    fill: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },

  calcIcon: {
    fill: theme.palette.grey[500],
    "&:hover": {
      fill: theme.palette.grey[400],
    },
  },

  hoverColor: {
    "&:hover": {
      fill: theme.palette.grey[500],
      color: theme.palette.grey[500],
    },
  },

  textDecorate: {
    textDecorationLine: "underline",
    textDecorationThickness: "initial",
    textDecorationStyle: "dotted",
    textUnderlineOffset: "0.1em",
  },

  infoBox: {
    borderRadius: "14px",
    border: `1px solid ${theme.palette.grey[300]}`,
    minHeight: "124px",
    height: "100%",
    padding: theme.spacing(2),
  },

  infoBoxTitle: {
    textTransform: "uppercase",
    fontWeight: 500,
    lineHeight: 1,
    color: theme.palette.grey[500],
    marginBottom: theme.spacing(1),
  },

  infoBoxBTN: {
    boxShadow: "none",
    borderRadius: "14px",
    minWidth: "max-content",
  },

  infoBoxCaption: {
    color: theme.palette.grey[500],
  },

  infoLoadingBTNBox: {
    margin: "0px -8px",
  },

  infoBoxLink: {
    color: `${theme.palette.primary.main} !important`,
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  infoBoxRefreshIcon: {
    fill: `${theme.palette.primary.main}`,
    marginLeft: theme.spacing(1),
    cursor: "pointer",
  },
  grid: {
    display: "flex",
    flexFlow: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  input: {
    ...theme.typography.h5,
    width: "100%",
  },
  fab_max: {
    minWidth: "initial",
    height: "initial",
    fontSize: "10px",
    padding: "0px 4px",
    marginLeft: theme.spacing(0.5),
    boxShadow: "none",
    borderRadius: theme.spacing(1),
  },

  liquidityLink: {
    color: "inherit",
    textDecoration: "none",
    "&:hover": {
      color: `${theme.palette.primary.main} !important`,
    },
  },
}));

const MuiAccordion = withStyles((theme) => ({
  root: {
    border: `1px solid rgba(0, 0, 0, .125)`,
    boxShadow: "none",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "auto",
    },
  },
  expanded: {},
}))(Accordion);

const MuiAccordionSummary = withStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    minHeight: 56,
    "&$expanded": {
      minHeight: 56,
    },
  },
  content: {
    "&$expanded": {
      margin: "12px 0",
    },
  },
  expanded: {},
}))(AccordionSummary);

const MuiAccordionDetails = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(AccordionDetails);

function StackingListView({
  item,
  network,
  active,
  handleChange,
  expired,
  farm,
}) {
  const [iconImage] = useIcon();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [dialogOpen, setDialogOpen] = useState(false);

  const [loading, setLoading] = useState({
    stack: false,
    harvest: false,
    earned: false,
  });
  const [supplyLoading, setSupplyLoading] = useState(false);
  const [supply, setSupply] = useState([0, 0, 0]);
  const [earned, setEarned] = useState([0, 0, 0]);
  const [stakedBefore, setStakedBefore] = useState(0);
  const [stakeAmount, setStackAmount] = useState("0");

  const [apr, setApr] = useState(0);
  const [aprLoading, setAprLoading] = useState(false);
  const [rewardPerSeconds, setRewardPerSeconds] = useState(0);
  const [remainingReward, setRemainingReward] = useState(0);

  // Stores the input and output for the liquidity removal preview
  const [stakedTokensOut, setStakedTokensOut] = useState([0, 0, 0]);

  const [coin1, setCoin1] = useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
    decimal: undefined,
  });
  const [coin2, setCoin2] = useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
    decimal: undefined,
  });

  // Determines whether the button should be enabled or not
  const isStackButtonEnabled = () => {
    // If both coins have been selected, and a valid float has been entered which is less than the user's balance, then return true
    const parsedInput = parseFloat(stakeAmount);
    return (
      !expired &&
      coin2.address &&
      !isNaN(parsedInput) &&
      0 < parsedInput &&
      parsedInput <= coin2.balance
    );
  };

  // Determines whether the button should be enabled or not
  const isHarvestButtonEnabled = () => {
    // If both coins have been selected, and a valid float has been entered which is less than the user's balance, then return true
    const parsedInput = parseFloat(earned[0]);
    return coin1.address && !isNaN(parsedInput) && 0 < parsedInput;
  };

  const stack = () => {
    console.log("Attempting to token stacking...");
    let loadings = { ...loading };
    loadings.stack = true;
    setLoading(loadings);

    stackToken(
      network.account,
      coin2.address,
      `${stakeAmount}`,
      item.id,
      network.stacking,
      network.signer,
      (res) => {
        if (res.status) {
          let loadings = { ...loading };
          loadings.stack = false;
          setLoading(loadings);
          set_staked_amount("", coin2.decimal);

          enqueueSnackbar("Stack Successful", {
            variant: "success",
            autoHideDuration: 5000,
          });

          refreshPoolLP(network.chainID);
        } else {
          let loadings = { ...loading };
          loadings.stack = false;
          setLoading(loadings);
          enqueueSnackbar("Stack Failed (" + res.e.reason + ")", {
            variant: "error",
            autoHideDuration: 5000,
          });
        }
      }
    ).catch((e) => {
      let loadings = { ...loading };
      loadings.stack = false;
      setLoading(loadings);
      enqueueSnackbar("Stack Failed (" + e.reason + ")", {
        variant: "error",
        autoHideDuration: 5000,
      });
    });
  };

  const harvest = () => {
    console.log("Attempting to token Harvest...");
    let loadings = { ...loading };
    loadings.harvest = true;
    setLoading(loadings);

    harvestToken(network.stacking, item.id, network.signer)
      .then(() => {
        let loadings = { ...loading };
        loadings.harvest = false;
        setLoading(loadings);
        setEarned([0, 0, 0]);

        enqueueSnackbar("Harvest Successful", {
          variant: "success",
          autoHideDuration: 5000,
        });
      })
      .catch((e) => {
        let loadings = { ...loading };
        loadings.harvest = false;
        setLoading(loadings);
        enqueueSnackbar("Harvest Failed (" + e.reason + ")", {
          variant: "error",
          autoHideDuration: 5000,
        });
      });
  };

  const get_earned = async () => {
    let loadings = { ...loading };
    loadings.earned = true;
    setLoading(loadings);

    // Get Earned
    await getEarned(
      network.stacking,
      item.id,
      network.account,
      network.signer
    ).then(async (res) => {
      if (res) {
        await parseTokenDecimal(
          item.rewards_token.address,
          `${res}`,
          network.signer
        ).then((res) => {
          if (
            item.rewards_token.lptokens &&
            item.rewards_token.lptokens.token0.address &&
            item.rewards_token.lptokens.token1.address
          ) {
            quoteRemoveLiquidity(
              item.rewards_token.lptokens.token0.address,
              item.rewards_token.lptokens.token1.address,
              res,
              network.factory,
            ).then((data) => {
              setEarned([
                Number(data[0])
                  .toFixed(+item.rewards_token.decimals)
                  .replace(/\.?0+$/, ""),
                data[1],
                data[2],
              ]);
            });
          } else {
            setEarned([res, 0, 0]);
          }
        });
      }
    });

    setTimeout(() => {
      let loadings = { ...loading };
      loadings.earned = false;
      setLoading(loadings);
    }, 1000);
  };

  const get_staked_before = async () => {
    // Get Staked Before
    await getStakedBefore(
      network.stacking,
      item.id,
      network.account,
      network.signer
    ).then((res) => {
      if (res) {
        // format Staked before
        parseTokenDecimal(
          item.staking_token.address,
          `${res}`,
          network.signer
        ).then((res) => {
          setStakedBefore(
            Number(res)
              .toFixed(+item.staking_token.decimals)
              .replace(/\.?0+$/, "")
          );
        });
      }
    });
  };

  const calcRewardPerSeconds = async () => {
    let currentTimeStamp = moment().unix();
    if (!expired) {
      const finishTime = +item.period_finish - currentTimeStamp;

      await parseTokenDecimal(
        item.rewards_token.address,
        item.reward_rate,
        network.signer
      ).then((res) => {
        setRewardPerSeconds(+res);
        setRemainingReward(finishTime * +res);
        calcApr(+res);
      });
    }
  };

  const calcApr = async (rps) => {
    if (+item.total_supply) {
      // token + token
      if (
        item.rewards_token.type === "token" &&
        item.staking_token.type === "token"
      ) {
        await getAmountOut(
          item.rewards_token.address,
          item.staking_token.address,
          "1",
          network.router,
        )
          .then(async (amount) => {
            await parseTokenDecimal(
              item.staking_token.address,
              `${item.total_supply}`,
              network.signer
            ).then((res) => {
              setApr(
                Number((amount * rps * (3600 * 24 * 365)) / +res).toFixed(2)
              );
            });
          })
          .catch((e) => {
            console.log(e);
          });
      }

      // token + lptoken || lpToken + lptoken
      if (
        item.rewards_token.type === "lptoken" ||
        item.staking_token.type === "lptoken"
      ) {
        console.log("----- start for set apr -----");

        let rewardAddressArray = [];
        let stakingAddressArray = [];
        let loopCount = 0;
        let totalPrice = 0;
        let avaragePrice = 0;

        // set array for reward tokens
        if (item.rewards_token.type === "lptoken") {
          rewardAddressArray.push(item.rewards_token.lptokens.token0.address);
          rewardAddressArray.push(item.rewards_token.lptokens.token1.address);
        } else {
          rewardAddressArray.push(item.rewards_token.address);
        }

        // set array for staking tokens
        if (item.staking_token.type === "lptoken") {
          stakingAddressArray.push(item.staking_token.lptokens.token0.address);
          stakingAddressArray.push(item.staking_token.lptokens.token1.address);
        } else {
          stakingAddressArray.push(item.staking_token.address);
        }

        // loop for get amount out price
        for (let r = 0; r < rewardAddressArray.length; r++) {
          const reward = rewardAddressArray[r];
          for (let s = 0; s < stakingAddressArray.length; s++) {
            const staking = stakingAddressArray[s];
            await getAmountOut(
              reward,
              staking,
              "1",
              network.router,
              network.signer
            ).then((amount) => {
              loopCount++;
              console.log(`price for ${reward} - ${staking}`, amount);
              if (amount) {
                totalPrice += +Number(amount).toFixed(7);
              }
            });
          }
        }

        console.log("total price", totalPrice);

        // set avarage price
        avaragePrice = totalPrice / loopCount;

        console.log("avarage price", avaragePrice);

        console.log("reward per seconds", Number(rps).toFixed(18));

        // parse total supply and set apr
        await parseTokenDecimal(
          item.staking_token.address,
          `${item.total_supply}`,
          network.signer
        ).then((res) => {
          console.log("total supply", Number(res).toFixed(18));
          setApr(
            Number((avaragePrice * rps * (3600 * 24 * 365)) / +res).toFixed(2)
          );
        });
      }
    }
  };

  const set_staked_amount = (balance, decimal) => {
    if (balance) {
      setStackAmount(balance);

      if (
        item.staking_token.lptokens &&
        item.staking_token.lptokens.token0.address &&
        item.staking_token.lptokens.token1.address &&
        balance
      ) {
        quoteRemoveLiquidity(
          item.staking_token.lptokens.token0.address,
          item.staking_token.lptokens.token1.address,
          `${balance}`,
          network.factory,
        ).then((data) => {
          setStakedTokensOut(data);
        });
      } else {
        setStakedTokensOut([0, 0, 0]);
      }
    }
  };

  const getDataAfterClose = () => {
    setDialogOpen(false);

    setTimeout(() => {
      get_earned();
      get_staked_before();
    }, 13000);
  };

  useEffect(async () => {
    setSupplyLoading(true);
    setAprLoading(true);
    await getBalanceAndSymbol(
      network.account,
      item.rewards_token.address,
      network.wethAddress,
      network.coins
    ).then((data) => {
      setCoin1({
        address: item.rewards_token.address,
        symbol: item.rewards_token.symbol,
        balance: data.balance,
        decimal: +item.rewards_token.decimals,
      });
    });

    await getBalanceAndSymbol(
      network.account,
      item.staking_token.address,
      network.wethAddress,
      network.coins
    ).then((data) => {
      setCoin2({
        address: item.staking_token.address,
        symbol: item.staking_token.symbol,
        balance: data.balance,
        decimal: +item.staking_token.decimals,
      });

      if (!expired) {
        set_staked_amount(
          Number(data.balance)
            .toFixed(+item.staking_token.decimals)
            .replace(/\.?0+$/, ""),
          +item.staking_token.decimals
        );
      }
    });

    // format total supply
    await parseTokenDecimal(
      item.staking_token.address,
      `${item.total_supply}`,
      network.signer
    ).then((res) => {
      if (
        item.staking_token.lptokens &&
        item.staking_token.lptokens.token0.address &&
        item.staking_token.lptokens.token1.address
      ) {
        quoteRemoveLiquidity(
          item.staking_token.lptokens.token0.address,
          item.staking_token.lptokens.token1.address,
          res,
          network.factory,
        ).then((data) => {
          setSupply(data);
        });
      } else {
        setSupply([res, 0, 0]);
      }
    });

    setSupplyLoading(false);

    await get_earned();

    await get_staked_before();

    await calcRewardPerSeconds();

    setAprLoading(false);
  }, []);

  useEffect(() => {
    if (active) {
      if (!expired) {
        set_staked_amount(
          Number(coin2.balance)
            .toFixed(coin2.decimal)
            .replace(/\.?0+$/, ""),
          coin2.decimal
        );
      }
    }
  }, [active]);

  return (
    <>
      <UnStackDialog
        open={dialogOpen}
        onClose={() => getDataAfterClose()}
        tokenAddress={coin2.address}
        stakedBefore={stakedBefore}
        poolIndex={item.id}
        stacking={network.stacking}
        signer={network.signer}
        chainID={network.chainID}
      />
      <MuiAccordion
        square
        expanded={active}
        onChange={handleChange(`panel${item.id}`)}
      >
        <MuiAccordionSummary
          aria-controls={`panel${item.id}`}
          id={`panel${item.id}d-header`}
        >
          <table className="w-100 stacking-list">
            <tbody>
              <tr>
                <td className="name-icon">
                  <div className="d-flex align-items-center">
                    <div className="multi-image-box">
                      {item.rewards_token.type === "token" ? (
                        <img
                          src={iconImage(item.rewards_token.symbol)}
                          alt={item.rewards_token.symbol}
                          className="image1"
                        />
                      ) : (
                        <>
                          <img
                            src={iconImage(
                              item.rewards_token.lptokens.token0.symbol
                            )}
                            alt={item.rewards_token.lptokens.token0.symbol}
                            className="image1"
                          />
                          <img
                            src={iconImage(
                              item.rewards_token.lptokens.token1.symbol
                            )}
                            alt={item.rewards_token.lptokens.token1.symbol}
                            className="image2"
                          />
                        </>
                      )}

                      {item.staking_token.type === "token" ? (
                        <img
                          src={iconImage(item.staking_token.symbol)}
                          alt={item.staking_token.symbol}
                          className="image3"
                        />
                      ) : (
                        <>
                          <img
                            src={iconImage(
                              item.staking_token.lptokens.token0.symbol
                            )}
                            alt={item.staking_token.lptokens.token0.symbol}
                            className="image3"
                          />
                          <img
                            src={iconImage(
                              item.staking_token.lptokens.token1.symbol
                            )}
                            alt={item.staking_token.lptokens.token1.symbol}
                            className="image4"
                          />
                        </>
                      )}
                    </div>

                    <div className="ms-2 d-flex flex-column">
                      <Typography variant="caption">
                        Earn{" "}
                        {item.rewards_token.type === "token"
                          ? item.rewards_token.symbol
                          : `${item.rewards_token.lptokens.token0.symbol}-${item.rewards_token.lptokens.token1.symbol}`}
                      </Typography>
                      <Typography variant="caption">
                        Stake{" "}
                        {item.staking_token.type === "token"
                          ? item.staking_token.symbol
                          : `${item.staking_token.lptokens.token0.symbol}-${item.staking_token.lptokens.token1.symbol}`}
                      </Typography>
                    </div>
                  </div>
                </td>
                <td className="apr">
                  <Typography variant="caption" className="lh-1">
                    APR
                  </Typography>
                  <div className="d-flex align-items-start">
                    <Typography
                      variant="body1"
                      // text-decoration-line-through
                      className={`lh-1 fw-bold d-flex align-items-center ${classes.hoverColor}`}
                    >
                      <span className={`me-2 ${classes.textDecorate}`}>
                        {expired ? (
                          <>{apr}%</>
                        ) : (
                          <>
                            {aprLoading ? (
                              <CircularProgress color="primary" size="15px" />
                            ) : (
                              <>
                                {+apr ? (
                                  <>{apr}%</>
                                ) : (
                                  <>
                                    {remainingReward}{" "}
                                    {item.rewards_token.type === "token"
                                      ? item.rewards_token.symbol
                                      : `${item.rewards_token.lptokens.token0.symbol}-${item.rewards_token.lptokens.token1.symbol}`}{" "}
                                    <Moment unix fromNow>
                                      {item.period_finish}
                                    </Moment>
                                  </>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </span>
                      <svg
                        className={`d-none d-sm-inline-block ${classes.calcIcon}`}
                        viewBox="0 0 24 24"
                        width="18px"
                        color="text"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z"></path>
                        <path d="M11.25 7.72H6.25V9.22H11.25V7.72Z"></path>
                        <path d="M18 15.75H13V17.25H18V15.75Z"></path>
                        <path d="M18 13.25H13V14.75H18V13.25Z"></path>
                        <path d="M8 18H9.5V16H11.5V14.5H9.5V12.5H8V14.5H6V16H8V18Z"></path>
                        <path d="M14.09 10.95L15.5 9.54L16.91 10.95L17.97 9.89L16.56 8.47L17.97 7.06L16.91 6L15.5 7.41L14.09 6L13.03 7.06L14.44 8.47L13.03 9.89L14.09 10.95Z"></path>
                      </svg>
                    </Typography>
                  </div>
                </td>
                <td className="liquidity">
                  <Typography variant="caption" className="lh-1">
                    Supply
                  </Typography>
                  {supplyLoading ? (
                    <Typography variant="body1" className="lh-1 fw-bold">
                      <CircularProgress color="primary" size="15px" />
                    </Typography>
                  ) : (
                    <>
                      {!item.staking_token.lptokens ? (
                        <Typography variant="body1" className="lh-1 fw-bold">
                          {supply[0]} {item.staking_token.symbol}
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="body1" className="lh-1 fw-bold">
                            {Number(supply[1])
                              .toFixed(
                                item.staking_token.lptokens.token0.decimals
                              )
                              .replace(/\.?0+$/, "")}{" "}
                            {item.staking_token.lptokens.token0.symbol}
                          </Typography>
                          <Typography variant="body1" className="lh-1 fw-bold">
                            {Number(supply[2])
                              .toFixed(
                                item.staking_token.lptokens.token1.decimals
                              )
                              .replace(/\.?0+$/, "")}{" "}
                            {item.staking_token.lptokens.token1.symbol}
                          </Typography>
                        </>
                      )}
                    </>
                  )}
                </td>
                <td className="period">
                  <Typography variant="caption" className="lh-1">
                    Expire
                  </Typography>
                  <Typography variant="body1" className="lh-1 fw-bold">
                    <Moment unix fromNow>
                      {item.period_finish}
                    </Moment>
                  </Typography>
                </td>
                <td className="detail">
                  <KeyboardArrowDownIcon className={classes.primaryText} />
                </td>
              </tr>
            </tbody>
          </table>
        </MuiAccordionSummary>
        <MuiAccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} className={classes.infoBox}>
                <Typography
                  variant="subtitle2"
                  className={classes.infoBoxTitle}
                >
                  {item.rewards_token.type === "token"
                    ? item.rewards_token.symbol
                    : `${item.rewards_token.lptokens.token0.symbol}-${item.rewards_token.lptokens.token1.symbol}`}{" "}
                  Earned
                </Typography>

                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <Typography
                      variant="h6"
                      className="fw-bolder d-flex align-items-center"
                    >
                      {earned[0]}
                      <CachedIcon
                        className={`${loading.earned ? "syncLoading" : ""} ${
                          classes.infoBoxRefreshIcon
                        }`}
                        onClick={() => get_earned()}
                      />
                    </Typography>
                    {item.rewards_token.lptokens && (
                      <div>
                        <Typography variant="caption">
                          {Number(earned[1])
                            .toFixed(
                              +item.rewards_token.lptokens.token0.decimals
                            )
                            .replace(/\.?0+$/, "")}{" "}
                          {item.rewards_token.lptokens.token0.symbol}
                        </Typography>
                        {" | "}
                        <Typography variant="caption">
                          {Number(earned[2])
                            .toFixed(
                              +item.rewards_token.lptokens.token1.decimals
                            )
                            .replace(/\.?0+$/, "")}{" "}
                          {item.rewards_token.lptokens.token1.symbol}
                        </Typography>
                      </div>
                    )}
                  </div>

                  <div className={classes.infoLoadingBTNBox}>
                    <LoadingButton
                      loading={loading.harvest}
                      valid={isHarvestButtonEnabled()}
                      success={false}
                      fail={false}
                      onClick={harvest}
                    >
                      Harvest
                    </LoadingButton>{" "}
                  </div>
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} className={classes.infoBox}>
                <Typography
                  variant="subtitle2"
                  className={classes.infoBoxTitle}
                >
                  {item.rewards_token.type === "token"
                    ? item.rewards_token.symbol
                    : `(${item.rewards_token.lptokens.token0.symbol}-${item.rewards_token.lptokens.token1.symbol})`}
                  -
                  {item.staking_token.type === "token"
                    ? item.staking_token.symbol
                    : `(${item.staking_token.lptokens.token0.symbol}-${item.staking_token.lptokens.token1.symbol})`}{" "}
                  Stake
                </Typography>

                <Typography variant="subtitle2" className="fw-bolder">
                  {item.staking_token.type === "token"
                    ? item.staking_token.symbol
                    : `${item.staking_token.lptokens.token0.symbol}-${item.staking_token.lptokens.token1.symbol}`}{" "}
                  Staked : {stakedBefore}{" "}
                  {!!stakedBefore && (
                    <Fab
                      variant="extended"
                      color="primary"
                      className={classes.fab_max}
                      onClick={() => setDialogOpen(true)}
                    >
                      UnStake
                    </Fab>
                  )}
                </Typography>

                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  className={classes.grid}
                >
                  {/* Text Field */}
                  <div>
                    <InputBase
                      disabled={expired}
                      value={stakeAmount}
                      onChange={(e) =>
                        set_staked_amount(e.target.value, coin2.decimal)
                      }
                      placeholder="0.0"
                      classes={{
                        root: classes.input,
                        input: classes.inputBase,
                      }}
                    />

                    {!!+stakedTokensOut[0] && (
                      <div>
                        <Typography variant="caption">
                          {Number(stakedTokensOut[1])
                            .toFixed(
                              +item.staking_token.lptokens.token0.decimals
                            )
                            .replace(/\.?0+$/, "")}{" "}
                          {item.staking_token.lptokens.token0.symbol}
                        </Typography>
                        {" | "}
                        <Typography variant="caption">
                          {Number(stakedTokensOut[2])
                            .toFixed(
                              +item.staking_token.lptokens.token1.decimals
                            )
                            .replace(/\.?0+$/, "")}{" "}
                          {item.staking_token.lptokens.token1.symbol}
                        </Typography>
                      </div>
                    )}
                  </div>

                  {/* Button */}

                  <div className={classes.infoLoadingBTNBox}>
                    <LoadingButton
                      loading={loading.stack}
                      valid={isStackButtonEnabled()}
                      success={false}
                      fail={false}
                      onClick={stack}
                    >
                      {loading.stack ? "processing" : "Stake"}
                    </LoadingButton>{" "}
                  </div>
                </Grid>
                {!!coin2.balance && (
                  <div className="d-flex align-items-center justify-content-end mt-1">
                    <span>
                      Balance:{" "}
                      {Number(coin2.balance)
                        .toFixed(coin2.decimal)
                        .replace(/\.?0+$/, "")}
                    </span>

                    {!expired && (
                      <Fab
                        variant="extended"
                        color="primary"
                        className={classes.fab_max}
                        onClick={() =>
                          set_staked_amount(
                            Number(coin2.balance)
                              .toFixed(coin2.decimal)
                              .replace(/\.?0+$/, ""),
                            coin2.decimal
                          )
                        }
                      >
                        Max
                      </Fab>
                    )}
                  </div>
                )}
              </Paper>
            </Grid>

            {farm && (
              <Grid item xs={12}>
                <Grid container spacing={2} className="justify-content-end">
                  <Grid item xs={12} sm={6}>
                    <div className="d-flex">
                      <Typography variant="body2">
                        <Link to="/liquidity" className={classes.liquidityLink}>
                          Deploy Liquidity
                        </Link>
                      </Typography>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </MuiAccordionDetails>
      </MuiAccordion>
    </>
  );
}

StackingListView.propTypes = {};

export default StackingListView;
